import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, Crown, Home as HomeIcon, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { getPromoConfig, globalCta } from "@/lib/promo";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { useTheme } from "@/lib/theme";
import { NewsRadar } from "@/components/NewsRadar";
import { useSeo, websiteJsonLd } from "@/lib/seo";
import CityLinks from "@/components/CityLinks";
import { PartnerLogoCarousel } from "@/components/PartnerLogoCarousel";

const HERO_IMAGE_DAY = "https://customer-assets-7cd3h4nn.emergentagent.net/job_real-estate-pro-101/artifacts/cqhd1u5t_444.png";
const HERO_IMAGE_NIGHT = "https://customer-assets-7cd3h4nn.emergentagent.net/job_real-estate-pro-101/artifacts/719fb55s_foto%20test%20sfondo%20sito.png";
const EDITORIAL_IMAGE = "https://images.unsplash.com/photo-1767950470198-c9cd97f8ed87?auto=format&fit=crop&w=2000&q=80";

export default function Landing() {
    const { isDark } = useTheme();
    useSeo({
        title: "VendiDirect - Il futuro dell'agenzia immobiliare con l'IA",
        description: "Marketplace immobiliare e CRM avanzato. Trova lead dai social e dalle aste, genera valutazioni professionali e gestisci tutto il tuo ufficio con la potenza dell'intelligenza artificiale.",
        canonical: "/",
        jsonLd: websiteJsonLd(),
    });
    const [city, setCity] = useState("");
    const [type, setType] = useState("");
    const [featured, setFeatured] = useState([]);
    const [featuredStats, setFeaturedStats] = useState({});
    const [trending, setTrending] = useState([]);
    const [segment, setSegment] = useState("all"); // all | luxury | standard
    const [heroCta, setHeroCta] = useState("Inizia ora →");
    const navigate = useNavigate();

    useEffect(() => {
        getPromoConfig().then((cfg) => setHeroCta(globalCta(cfg)));
    }, []);

    useEffect(() => {
        const loadTrending = () => api.get("/properties/trending").then((r) => setTrending(r.data.items || [])).catch(() => {});
        loadTrending();
        const iv = setInterval(loadTrending, 60000); // live: aggiorna ogni 60s
        return () => clearInterval(iv);
    }, []);

    const loadFeatured = (seg) => {
        const params = {};
        if (seg === "luxury") params.luxury = true;
        if (seg === "standard") params.luxury = false;
        const url = seg === "all" ? "/properties/featured" : "/properties";
        api.get(url, { params: seg === "all" ? {} : params }).then((r) => {
            const items = (r.data.items || []).slice(0, 6);
            setFeatured(items);
            const ids = items.map((p) => p.id);
            if (ids.length > 0) {
                api.post("/properties/stats-batch", { ids })
                    .then((sr) => setFeaturedStats(sr?.data?.stats || {}))
                    .catch(() => setFeaturedStats({}));
            }
        }).catch(() => {});
    };

    useEffect(() => { loadFeatured(segment); }, [segment]);

    const search = (e) => {
        e?.preventDefault();
        const params = new URLSearchParams();
        if (city) params.set("city", city);
        if (type) params.set("property_type", type);
        navigate(`/annunci?${params.toString()}`);
    };

    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section data-testid="hero" className="relative min-h-[46vh] lg:min-h-[54vh] grain overflow-hidden">
                {/* Skyline diurna (tema chiaro) */}
                <img
                    src={HERO_IMAGE_DAY}
                    alt=""
                    data-testid="hero-image-day"
                    fetchPriority={isDark ? "low" : "high"}
                    loading={isDark ? "lazy" : "eager"}
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1200ms] ease-in-out ${isDark ? "opacity-0" : "opacity-100"}`}
                />
                {/* Skyline notturna di Roma (tema scuro) — cross-fade automatico */}
                <img
                    src={HERO_IMAGE_NIGHT}
                    alt=""
                    data-testid="hero-image-night"
                    fetchPriority={isDark ? "high" : "low"}
                    loading={isDark ? "eager" : "lazy"}
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1200ms] ease-in-out ${isDark ? "opacity-100" : "opacity-0"}`}
                />
                {/* Overlay diurno: NESSUN filtro/gradiente extra su richiesta (27/08/2026) —
                    la foto "444.png" va mostrata con i suoi colori originali, senza overlay. */}
                {/* Overlay notturno: velo nero leggero (~25%) — la foto è già scura, basta poco per il testo bianco */}
                <div
                    data-testid="hero-overlay-night"
                    className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${isDark ? "opacity-100" : "opacity-0"}`}
                    style={{ backgroundImage: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.15) 55%, hsl(var(--ink-950) / 0.85) 100%)" }}
                />
                <div className="relative w-full px-6 md:px-12 lg:px-20 2xl:px-28 pt-28 lg:pt-32 pb-10">
                    <div className="text-center fade-up mb-10">
                        <h1 data-testid="hero-brand-title" className="hero-title-shadow font-serif text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight">
                            <span className="text-white">vendi</span><span className="hero-brand-gold">direct</span><span className="hero-brand-gold">.it</span>
                        </h1>
                        <p data-testid="hero-title" className="hero-title-shadow font-serif text-2xl sm:text-3xl lg:text-4xl text-white mt-5 leading-snug">
                            Vendi casa in modo semplice
                        </p>
                    </div>

                    <div className="max-w-2xl mx-auto fade-up">
                        <div data-testid="hero-vendi-casa-banner" className="border-2 border-gold bg-gradient-to-r from-gold/15 via-gold/10 to-transparent backdrop-blur-xl px-5 sm:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                                <Sparkles size={18} className="text-gold mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-1">Vuoi vendere o affittare casa?</div>
                                    <div className="text-sm text-ink-50 leading-snug">
                                        <span className="font-medium">Fatti richiamare da un&apos;agenzia della tua zona</span>{" "}
                                        <span className="text-ink-300">gratuitamente, senza impegno.</span>
                                    </div>
                                </div>
                            </div>
                            <Link
                                to="/vendi-casa"
                                data-testid="hero-vendi-casa-cta"
                                className="cta-gold cta-gold-pulse px-6 py-2.5 text-xs flex items-center gap-2 whitespace-nowrap w-full sm:w-auto justify-center"
                            >
                                Valutazione gratuita <ArrowRight size={13} />
                            </Link>
                        </div>

                        <form onSubmit={search} data-testid="hero-search-form" className="mt-6 bg-black/60 backdrop-blur-xl border border-white/10 p-2 flex flex-col md:flex-row gap-2">
                            <div className="flex items-center gap-3 px-4 py-3 flex-1 border-b md:border-b-0 md:border-r border-white/10">
                                <MapPin size={16} className="text-gold" />
                                <input
                                    data-testid="hero-search-city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Città (es. Milano, Roma...)"
                                    className="bg-transparent outline-none w-full text-ink-50 placeholder:text-ink-400 text-sm"
                                />
                            </div>
                            <select
                                data-testid="hero-search-type"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="bg-transparent outline-none px-4 py-3 text-sm text-ink-50 md:border-r border-white/10"
                            >
                                <option value="" className="bg-ink-950">Tipologia</option>
                                <option value="appartamento" className="bg-ink-950">Appartamento</option>
                                <option value="villa" className="bg-ink-950">Villa</option>
                                <option value="attico" className="bg-ink-950">Attico</option>
                                <option value="loft" className="bg-ink-950">Loft</option>
                                <option value="casa" className="bg-ink-950">Casa</option>
                            </select>
                            <button
                                type="submit"
                                data-testid="hero-search-button"
                                className="cta-gold cta-gold-pulse px-8 py-3 text-sm flex items-center justify-center gap-2"
                            >
                                <Search size={16} /> Cerca casa
                            </button>
                        </form>

                        <div data-testid="hero-publish-banner" className="mt-6 border border-gold/40 bg-black/50 backdrop-blur-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div>
                                <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Vendi anche tu?</span>
                                <span className="block sm:inline sm:ml-3 text-sm text-ink-100">Pubblica la tua casa in 5 minuti — <span className="text-ink-400">nessuna commissione</span></span>
                            </div>
                            <Link to="/pubblica" data-testid="hero-publish-cta" className="cta-gold px-5 py-2 text-xs whitespace-nowrap">
                                Pubblica gratis →
                            </Link>
                        </div>

                    </div>
                </div>
            </section>

            <PartnerLogoCarousel />

            <NewsRadar />

            {/* TRENDING NOW — top 5 annunci più visti nelle ultime 24h, live */}
            {trending.length > 0 && (
                <section data-testid="trending-now-section" className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 pt-20 lg:pt-24">
                    <div className="flex items-center gap-3 mb-10">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-orange-500/15 text-orange-400"><TrendingUp size={16} /></span>
                        <div>
                            <span className="label-eyebrow text-orange-400">In tempo reale</span>
                            <h2 className="font-serif text-3xl sm:text-4xl text-ink-50 mt-1">Trending Now</h2>
                        </div>
                    </div>
                    <Carousel opts={{ align: "start", loop: trending.length > 3 }} className="w-full">
                        <CarouselContent className="-ml-6">
                            {trending.map((p, i) => (
                                <CarouselItem key={p.id} data-testid={`trending-item-${p.id}`} className="pl-6 basis-full sm:basis-1/2 lg:basis-1/3">
                                    <PropertyCard property={p} index={i} stats={{ views_24h: p.views_24h }} />
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        {trending.length > 3 && (
                            <div className="flex justify-end gap-3 mt-6">
                                <CarouselPrevious data-testid="trending-prev" className="relative left-0 top-0 translate-y-0" />
                                <CarouselNext data-testid="trending-next" className="relative right-0 top-0 translate-y-0" />
                            </div>
                        )}
                    </Carousel>
                </section>
            )}

            {/* FEATURED LISTINGS */}
            <section className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 pt-6 lg:pt-8 pb-24 lg:pb-32">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
                    <div>
                        <span className="label-eyebrow">Selezione editoriale</span>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-3">Immobili in evidenza</h2>
                    </div>
                    <div data-testid="segment-toggle" className="inline-flex border border-white/10 self-start md:self-auto">
                        <button data-testid="seg-all" onClick={() => setSegment("all")} className={`px-5 py-2.5 text-sm flex items-center gap-2 transition-colors ${segment === "all" ? "bg-gold text-black" : "text-ink-100 hover:text-gold"}`}>Tutti</button>
                        <button data-testid="seg-luxury" onClick={() => setSegment("luxury")} className={`px-5 py-2.5 text-sm flex items-center gap-2 transition-colors border-l border-white/10 ${segment === "luxury" ? "bg-gold text-black" : "text-ink-100 hover:text-gold"}`}><Crown size={13} /> Lusso</button>
                        <button data-testid="seg-standard" onClick={() => setSegment("standard")} className={`px-5 py-2.5 text-sm flex items-center gap-2 transition-colors border-l border-white/10 ${segment === "standard" ? "bg-gold text-black" : "text-ink-100 hover:text-gold"}`}><HomeIcon size={13} /> Standard</button>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    {featured.map((p, i) => (
                        <PropertyCard key={p.id} property={p} index={i} stats={featuredStats[p.id]} />
                    ))}
                </div>
                <div className="text-center mt-12">
                    <Link to={segment === "luxury" ? "/annunci?luxury=true" : segment === "standard" ? "/annunci?luxury=false" : "/annunci"} data-testid="see-all-link" className="inline-flex items-center gap-2 text-sm text-ink-100 hover:text-gold transition-colors">
                        Vedi tutti gli annunci {segment !== "all" && (segment === "luxury" ? "di lusso" : "standard")} <ArrowRight size={14} />
                    </Link>
                </div>
            </section>

            {/* VENDI CASA CTA — dedicata al form pubblico /vendi-casa */}
            <section data-testid="vendi-casa-section" className="relative border-y border-gold/30 bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 grain">
                <div className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <span className="label-eyebrow text-gold flex items-center gap-2">
                                <Sparkles size={13} /> Vuoi vendere o affittare la tua casa?
                            </span>
                            <h2 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-4 leading-tight">
                                Racconta la tua casa in 2 minuti,<br />
                                <span className="italic text-gold">un&apos;agenzia della tua zona ti richiama.</span>
                            </h2>
                            <p className="text-ink-300 mt-6 leading-relaxed max-w-2xl">
                                Compila un modulo veloce con i dettagli del tuo immobile. Ti mettiamo in contatto con <span className="text-ink-50 font-medium">una sola agenzia certificata</span> della tua città — niente 30 telefonate, niente spam. Servizio 100% gratuito per il proprietario.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <Link
                                    to="/vendi-casa"
                                    data-testid="landing-vendi-casa-cta"
                                    className="cta-gold cta-gold-pulse px-8 py-3.5 text-sm flex items-center gap-2 whitespace-nowrap"
                                >
                                    Richiedi valutazione gratuita <ArrowRight size={14} />
                                </Link>
                                <div className="flex items-center gap-2 text-xs text-ink-400">
                                    <ShieldCheck size={13} className="text-gold" />
                                    I tuoi dati sono al sicuro, condivisi solo con l&apos;agenzia assegnata
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="border border-gold/40 bg-black/40 backdrop-blur-xl p-8 space-y-5">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 border border-gold flex items-center justify-center text-gold font-serif text-lg flex-shrink-0">1</div>
                                    <div>
                                        <div className="text-sm text-ink-50 font-medium">Descrivi l&apos;immobile</div>
                                        <div className="text-xs text-ink-400 mt-0.5">Città, tipologia, obiettivo (vendita o affitto).</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 border border-gold flex items-center justify-center text-gold font-serif text-lg flex-shrink-0">2</div>
                                    <div>
                                        <div className="text-sm text-ink-50 font-medium">Ti assegniamo un&apos;agenzia</div>
                                        <div className="text-xs text-ink-400 mt-0.5">Scelta automatica tra le agenzie certificate che coprono la tua zona.</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 border border-gold flex items-center justify-center text-gold font-serif text-lg flex-shrink-0">3</div>
                                    <div>
                                        <div className="text-sm text-ink-50 font-medium">Ricevi una valutazione</div>
                                        <div className="text-xs text-ink-400 mt-0.5">L&apos;agenzia ti contatta entro 24 ore per valutare la casa senza impegno.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ESPLORA PER CITTÀ — link alle pagine geografiche SEO */}
            <section data-testid="landing-cities" className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 pb-8">
                <span className="label-eyebrow">Esplora per città</span>
                <h2 className="font-serif text-base md:text-lg text-ink-50 mt-2 mb-6">Case in vendita e affitto nelle principali città</h2>
                <CityLinks limit={12} />
            </section>

            {/* TWO-COLUMN EDITORIAL */}
            <section className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 relative aspect-[4/3] overflow-hidden">
                        <img src={EDITORIAL_IMAGE} alt="Pubblica il tuo immobile su VendiDirect" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                    <div className="lg:col-span-5">
                        <span className="label-eyebrow">Per chi pubblica</span>
                        <h2 className="font-serif text-4xl text-ink-50 mt-3 leading-tight">
                            Pubblica il tuo immobile <span className="italic text-gold">come merita</span>.
                        </h2>
                        <p className="text-ink-400 mt-6 leading-relaxed">
                            Carica tutte le fotografie che vuoi, racconta la tua casa con un editoriale curato, scegli quali contatti rendere pubblici. La privacy del tuo annuncio è garantita: indirizzi e numeri sono visibili solo agli utenti abbonati.
                        </p>
                        <div className="mt-10 flex gap-4">
                            <Link to="/pubblica" data-testid="cta-pubblica" className="cta-gold cta-gold-pulse px-8 py-3 text-sm">
                                {heroCta}
                            </Link>
                            <Link to="/prezzi" data-testid="cta-prezzi" className="cta-outline px-8 py-3 text-sm">
                                Vedi abbonamenti
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
